import {Component, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Product, ProductService, RoutingService} from '@spartacus/core';
import {CommonConfigurator, ConfiguratorRouterExtractorService} from '@spartacus/product-configurator/common';
import {
  ConfiguratorCommonsService,
  ConfiguratorExpertModeService,
  ConfiguratorProductTitleComponent,
} from '@spartacus/product-configurator/rulebased';
import {Configurator} from '@spartacus/product-configurator/rulebased/public_api';
import {Observable, combineLatest, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {BACK_LINK_URL} from '../../guards';
import {SessionStorageService} from '../../services';
import tokens from '../../token/tokens';

const {CART_DECORATOR_ERROR_HANDLING_SERVICE_PROVIDER} = tokens.services;

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-product-title',
  templateUrl: './dcp-configurator-product-title.component.html',
  styleUrl: './dcp-configurator-product-title.component.scss',
})
export class DcpConfiguratorProductTitleComponent extends ConfiguratorProductTitleComponent {
  private cartDecoratorErrorHandlingService = inject(CART_DECORATOR_ERROR_HANDLING_SERVICE_PROVIDER);
  backLinkUrl: string | null = null;
  openConfirmationModal = false;

  constructor(
    protected override configuratorCommonsService: ConfiguratorCommonsService,
    protected override configRouterExtractorService: ConfiguratorRouterExtractorService,
    protected override productService: ProductService,
    protected override configExpertModeService: ConfiguratorExpertModeService,
    private sessionStorageService: SessionStorageService,
    private route: ActivatedRoute,
    private routingService: RoutingService,
  ) {
    super(configuratorCommonsService, configRouterExtractorService, productService, configExpertModeService);
    this.backLinkUrl = this.sessionStorageService.getItem(BACK_LINK_URL);
  }

  hasError$: Observable<boolean> = this.cartDecoratorErrorHandlingService.getErrorState$().pipe(map((e) => e.hasError));

  isEditMode(): Observable<boolean> {
    return this.configRouterExtractorService.extractRouterData().pipe(
      map((routingData) => routingData.owner.type),
      map((ownerType) => ownerType !== CommonConfigurator.OwnerType.CART_ENTRY),
    );
  }

  isBackToSolutionParameterDisabled(): Observable<boolean> {
    return this.route.queryParams.pipe(map((params) => params['back'] === '0'));
  }

  showBackToSolutionLink(): Observable<boolean> {
    return combineLatest([this.isEditMode(), this.isBackToSolutionParameterDisabled(), this.configuration$]).pipe(
      map(
        ([isEditMode, backParameterDisabled, configuration]) => isEditMode && !backParameterDisabled && !!configuration,
      ),
    );
  }

  getBackToSolutionPageURL(): Observable<string | null> {
    return this.configuration$.pipe(
      switchMap((configuration: Configurator.Configuration | null) => {
        return of(this.backLinkUrl ?? (configuration?.pdpUrl || null));
      }),
    );
  }

  onLinkClick(event: Event) {
    event.preventDefault();
    this.openConfirmationModal = true;
  }

  onConfirm(product: Product) {
    this.openConfirmationModal = false;
    this.getBackToSolutionPageURL()
      .pipe(untilDestroyed(this))
      .subscribe((url: string | null) => {
        if (url) {
          window.location.assign(url);
        } else {
          this.routingService.go({
            cxRoute: 'product',
            params: product,
          });
        }
      });
  }

  onCancel() {
    this.openConfirmationModal = false;
  }
}
